import { useRef } from 'react'

import { IconPointerRight, Space, SubText, TextLabel } from '@procurify/ui'
import { upperFirst } from 'lodash'
import { nanoid } from 'nanoid'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { type AuditLogChangedField } from '../../entities/AuditLogChangedField'

type valueType = 'from' | 'to'

type ChangeValue = Nullable<AuditLogChangedField['to']>

export interface IAuditLogRecordChangeProps {
  field: AuditLogChangedField['field']
  from?: AuditLogChangedField['from']
  to: ChangeValue
}

const isEmptyValue = (value: ChangeValue) =>
  value === null || value === '' || (Array.isArray(value) && value.length === 0)

// The backend writes "empty" when there is no earlier version and "None" for a
// null value.
const BACKEND_EMPTY_VALUES = ['empty', 'None']

const isBlankValue = (value: ChangeValue | undefined) =>
  value === undefined ||
  isEmptyValue(value) ||
  (typeof value === 'string' && BACKEND_EMPTY_VALUES.includes(value))

/** A change from an empty value to an empty value records nothing. */
export const isEmptyChange = ({
  from,
  to,
}: Pick<IAuditLogRecordChangeProps, 'from' | 'to'>) =>
  isBlankValue(from) && isBlankValue(to)

const isAcronym = (word: string) =>
  !/[a-z]/.test(word) && (word.match(/[A-Z]/g) ?? []).length > 1

// Sentence case that keeps the acronyms in backend field names ("GL Code" reads "GL code").
const formatFieldName = (field: string) =>
  upperFirst(
    field
      .split(' ')
      .map((word) => (isAcronym(word) ? word : word.toLowerCase()))
      .join(' ')
  )

// Backend status values arrive lowercase ("added", "empty"). Only the first
// letter changes, so user-entered names and codes keep their casing.
const formatValueText = (value: string | number) =>
  typeof value === 'string' ? upperFirst(value) : value

export const getColorForValueType = (valueType: valueType) => {
  switch (valueType) {
    case 'from':
      return 'textSecondary'
    case 'to':
    default:
      return 'textPrimary'
  }
}

export const AuditLogRecordChange = ({
  field,
  from,
  to,
}: IAuditLogRecordChangeProps) => {
  const intl = useProcurifyIntl()
  // One page repeats field names across entries, and names can contain spaces,
  // so the label id can't be derived from the field (React 17 has no useId).
  const labelIdRef = useRef<string>(nanoid())

  const formatValue = (value: ChangeValue, valueType: valueType) => {
    const textColor = getColorForValueType(valueType)

    if (isEmptyValue(value))
      return (
        <SubText bold color={textColor}>
          {intl.formatMessage(messages.noneLabel)}
        </SubText>
      )

    if (Array.isArray(value)) {
      return (
        <ul>
          {value.map((item, index) => (
            <li key={index}>
              <SubText bold color={textColor}>
                {formatValueText(item)}
              </SubText>
            </li>
          ))}
        </ul>
      )
    }
    return (
      <SubText bold color={textColor}>
        {formatValueText(value)}
      </SubText>
    )
  }

  return (
    <>
      <TextLabel id={labelIdRef.current}>{formatFieldName(field)}</TextLabel>

      <div aria-labelledby={labelIdRef.current}>
        <Space direction='horizontal' align='center' size='small'>
          {!!from && (
            <Space direction='horizontal' align='center' size='small'>
              {formatValue(from, 'from')}
              <IconPointerRight />
            </Space>
          )}
          {formatValue(to, 'to')}
        </Space>
      </div>
    </>
  )
}
